import { useState, useEffect, useCallback } from 'react'

interface LightboxImage {
  url: string
  filename?: string
}

export interface UseLightboxReturn {
  isOpen: boolean
  currentImage: LightboxImage | null
  currentIndex: number
  images: LightboxImage[]
  open: (images: LightboxImage[], index?: number) => void
  close: () => void
  next: () => void
  prev: () => void
}

export function useLightbox(): UseLightboxReturn {
  const [isOpen, setIsOpen] = useState(false)
  const [images, setImages] = useState<LightboxImage[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)

  const open = useCallback((newImages: LightboxImage[], index = 0) => {
    setImages(newImages)
    setCurrentIndex(index)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const next = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
  }, [images.length])

  const prev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
  }, [images.length])

  // Handle ESC key
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close()
      } else if (e.key === 'ArrowRight' && images.length > 1) {
        next()
      } else if (e.key === 'ArrowLeft' && images.length > 1) {
        prev()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, close, next, prev, images.length])

  const currentImage = images[currentIndex] || null

  return {
    isOpen,
    currentImage,
    currentIndex,
    images,
    open,
    close,
    next,
    prev,
  }
}
